import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import DeleteAccount from '../components/DeleteAccount';

// Разовая покупка (Non-Consumable) через Google Play Billing / Apple In-App Purchase.
// Плагин: cordova-plugin-purchase (глобальный объект CdvPurchase в приложении).
const PRODUCTS = {
  google: { personal: 'personal', professional: 'professional' },
  apple:  { personal: 'com.estateua.astrodata.personal', professional: 'com.estateua.astrodata.professional' },
};
const TARIFFS = [
  { key: 'personal',     label: 'Личный',           desc: 'Только для себя — навсегда' },
  { key: 'professional', label: 'Профессиональный', desc: 'Неограниченно клиентов' },
];

const platformName = () => {
  const p = window.Capacitor?.getPlatform?.();
  return p === 'android' ? 'google' : p === 'ios' ? 'apple' : null;
};

export default function PaymentPage({ paid, onPaid, onLogout, onDelete }) {
  const navigate = useNavigate();
  const platform = platformName();
  const [status, setStatus] = useState(platform ? 'loading' : 'web'); // loading | ready | buying | verifying | failed | web
  const [error, setError] = useState('');
  const [prices, setPrices] = useState({});
  const storeRef = useRef(null);

  useEffect(() => {
    if (paid) { navigate('/dashboard'); return; }
    if (!platform) return;

    let cancelled = false;
    let started = false;
    const init = () => {
      if (started) return;
      started = true;
      const CdvPurchase = window.CdvPurchase;
      if (!CdvPurchase) { setStatus('failed'); setError('Покупки недоступны на этом устройстве.'); return; }
      const { store, ProductType, Platform } = CdvPurchase;
      storeRef.current = store;
      const plat = platform === 'google' ? Platform.GOOGLE_PLAY : Platform.APPLE_APPSTORE;
      store.register(Object.values(PRODUCTS[platform]).map(id => ({ id, type: ProductType.NON_CONSUMABLE, platform: plat })));

      store.when()
        .productUpdated(() => {
          const next = {};
          TARIFFS.forEach(t => {
            const p = store.get(PRODUCTS[platform][t.key], plat);
            const price = p?.offers?.[0]?.pricingPhases?.[0]?.price;
            if (price) next[t.key] = price;
          });
          if (!cancelled) { setPrices(next); setStatus(s => (s === 'loading' ? 'ready' : s)); }
        })
        .approved(async (tx) => {
          // Сервер сверяет покупку со стором и только после этого открывает доступ
          if (!cancelled) setStatus('verifying');
          try {
            const productId = tx.products?.[0]?.id;
            if (platform === 'google') {
              await api.verifyGoogle(productId, tx.nativePurchase?.purchaseToken || tx.purchaseId);
            } else {
              await api.verifyApple(tx.transactionId);
            }
            await tx.finish();
            if (!cancelled) { onPaid(); navigate('/dashboard'); }
          } catch (e) {
            if (!cancelled) {
              setStatus('failed');
              setError(e.response?.data?.detail || 'Не удалось подтвердить покупку. Если деньги списаны — нажмите «Восстановить покупку».');
            }
          }
        });

      store.initialize([plat]).then(() => { if (!cancelled) setStatus(s => (s === 'loading' ? 'ready' : s)); });
    };

    document.addEventListener('deviceready', init, { once: true });
    if (window.CdvPurchase) init();
    return () => { cancelled = true; };
  }, []);

  const buy = async (tariffKey) => {
    const store = storeRef.current;
    if (!store) return;
    setError(''); setStatus('buying');
    const { Platform } = window.CdvPurchase;
    const plat = platform === 'google' ? Platform.GOOGLE_PLAY : Platform.APPLE_APPSTORE;
    const product = store.get(PRODUCTS[platform][tariffKey], plat);
    const offer = product?.getOffer();
    if (!offer) { setStatus('failed'); setError('Товар пока недоступен в магазине. Попробуйте позже.'); return; }
    const err = await offer.order();
    if (err) {
      // 6777006 = пользователь отменил покупку
      setStatus(err.code === 6777006 ? 'ready' : 'failed');
      if (err.code !== 6777006) setError(err.message || 'Покупка не удалась.');
    }
  };

  const restore = async () => {
    const store = storeRef.current;
    if (!store) return;
    setError(''); setStatus('verifying');
    try { await store.restorePurchases(); } catch (e) { /* approved-обработчик сам обновит статус */ }
    setTimeout(() => setStatus(s => (s === 'verifying' ? 'ready' : s)), 8000);
  };

  return (
    <div style={styles.page}>
      <div style={styles.box}>
        <div style={styles.logo}>✦ AstraData</div>
        <p style={styles.text}>Откройте доступ к расчётам — разовая покупка, без подписки.</p>

        {status === 'web' && (
          <p style={styles.text}>Покупка доступна в мобильном приложении AstraData (Google Play / App Store).</p>
        )}
        {(status === 'loading' || status === 'verifying' || status === 'buying') && (
          <p style={styles.text}>{status === 'verifying' ? 'Проверяем покупку…' : status === 'buying' ? 'Оформляем…' : 'Загрузка…'}</p>
        )}

        {(status === 'ready' || status === 'failed') && TARIFFS.map(t => (
          <div key={t.key} style={styles.card}>
            <div style={styles.cardName}>{t.label}</div>
            <div style={styles.cardDesc}>{t.desc}</div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 10 }}
              onClick={() => buy(t.key)} disabled={!storeRef.current}>
              {prices[t.key] ? `Купить — ${prices[t.key]}` : 'Купить'}
            </button>
          </div>
        ))}

        {error && <p style={styles.error}>{error}</p>}

        {platform && (status === 'ready' || status === 'failed') && (
          <button className="btn btn-outline" style={{ width: '100%', marginTop: 8 }} onClick={restore}>
            Восстановить покупку
          </button>
        )}

        <button onClick={onLogout} style={styles.link}>Выйти</button>
        <DeleteAccount onDelete={onDelete} />
      </div>
    </div>
  );
}

const styles = {
  page:  { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  box:   { width: '100%', maxWidth: 420, background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 20, padding: 32, textAlign: 'center' },
  logo:  { fontSize: 22, fontWeight: 700, color: '#a78bfa', marginBottom: 16 },
  text:  { fontSize: 14, color: '#a0a0c0', marginBottom: 20 },
  error: { fontSize: 14, color: '#e74c3c', margin: '12px 0' },
  card:  { border: '1px solid #2a2a4a', borderRadius: 14, padding: 16, marginBottom: 12, background: '#16162a' },
  cardName: { fontSize: 17, fontWeight: 700, color: '#fff' },
  cardDesc: { fontSize: 13, color: '#a0a0c0', marginTop: 4 },
  link:  { display: 'block', margin: '18px auto 0', background: 'none', border: 'none', color: '#a78bfa', fontSize: 14, cursor: 'pointer' },
};
